import React, { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { deleteSchemaModelRequest } from "store/schemaModels/actions";
import { buildToolError } from "./errorEnvelope";
import { fetchSchemaById, type FetchedSchema } from "./schemaApi";
import { verifyDeletedSchema } from "./verify";
import type { ToolPartProps } from "./types";

interface DeleteSchemaInput {
    schemaId: string;
    schemaName: string;
}

interface DeleteSchemaOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    schemaId?: string;
    schemaName?: string;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "checking" | "ready" | "denied" | "fetch-error" | "deleting" | "deleted" | "delete-error";

export default function DeleteSchemaTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<DeleteSchemaInput, DeleteSchemaOutput>) {
    const { dispatch, addToolOutput, businessAreaId, tenantId, schemaList, deleteSchemaModelResult } = helpers;

    const [phase, setPhase] = useState<Phase>("checking");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const [fetchedSchema, setFetchedSchema] = useState<FetchedSchema | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    // Snapshot of the persistent deleteSchemaModelResult slice at dispatch time,
    // so a stale `{ success: true }` from a previous delete is ignored.
    const resultAtDispatchRef = useRef<unknown>(null);

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        if (!input?.schemaId) return;
        initialisedRef.current = true;

        const inList = schemaList.some((s) => s.schemaId === input.schemaId);
        if (!inList) {
            setPhase("denied");
            setDenyReason(`"${input.schemaName}" is not in your loaded schema list. Open the schema list and try again.`);
            return;
        }
        if (!tenantId || !businessAreaId) {
            setPhase("denied");
            setDenyReason("Tenant or business area context not loaded.");
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const schema = await fetchSchemaById(tenantId, businessAreaId, input.schemaId);
                if (cancelled) return;
                if (!schema) {
                    setPhase("denied");
                    setDenyReason("Schema not found on the server.");
                    return;
                }
                setFetchedSchema(schema);
                setPhase("ready");
            } catch (e: any) {
                if (cancelled) return;
                setPhase("fetch-error");
                setDenyReason(e?.message || "Failed to fetch schema details.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [state, input, schemaList, tenantId, businessAreaId]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied" || phase === "fetch-error") {
            resolvedRef.current = true;
            addToolOutput({
                tool: "deleteSchema",
                toolCallId,
                output: { success: false, error: denyReason || "Cannot delete schema." },
            });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "deleting") return;
        if (!deleteSchemaModelResult) return;
        if (deleteSchemaModelResult === resultAtDispatchRef.current) return;
        if (deleteSchemaModelResult.success === true) {
            (async () => {
                let stillThere: FetchedSchema | null = null;
                let fetchErr: string | null = null;
                try {
                    if (tenantId && businessAreaId && input?.schemaId) {
                        stillThere = await fetchSchemaById(tenantId, businessAreaId, input.schemaId);
                    }
                } catch (e: any) {
                    fetchErr = e?.message || "fetch error";
                }
                const report = verifyDeletedSchema(stillThere, input?.schemaName || input?.schemaId || "the schema");
                setPhase("deleted");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "deleteSchema",
                        toolCallId,
                        output: {
                            success: true,
                            schemaId: input?.schemaId,
                            schemaName: fetchedSchema?.name || input?.schemaName,
                            verified: !fetchErr && report.verified,
                            appliedChanges: report.appliedChanges,
                            unappliedChanges: fetchErr
                                ? [`could not re-fetch to verify deletion (${fetchErr})`]
                                : report.unappliedChanges,
                        },
                    });
                }
            })();
        } else if (deleteSchemaModelResult.success === false) {
            setPhase("delete-error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "deleteSchema",
                    toolCallId,
                    output: buildToolError(deleteSchemaModelResult, "Delete failed."),
                });
            }
        }
    }, [deleteSchemaModelResult, phase, fetchedSchema, input, toolCallId, addToolOutput]);

    const handleDelete = () => {
        if (!businessAreaId || !input?.schemaId) return;
        resultAtDispatchRef.current = deleteSchemaModelResult;
        setPhase("deleting");
        dispatch(deleteSchemaModelRequest(businessAreaId, input.schemaId));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "deleteSchema", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    const fieldCount = Array.isArray(fetchedSchema?.fields) ? fetchedSchema!.fields!.length : undefined;

    return (
        <div className="rounded-2xl border border-red-300 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-red-700">
                <DeleteOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Confirm deletion</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{fetchedSchema?.name || input?.schemaName || input?.schemaId}</div>
            <div className="text-[10px] text-zinc-500">
                id: {input?.schemaId}
                {fieldCount !== undefined && ` · ${fieldCount} fields`}
                {fetchedSchema?.version !== undefined && ` · v${fetchedSchema.version}`}
            </div>

            {phase === "checking" && (
                <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Loading schema details...
                </div>
            )}

            {(phase === "denied" || phase === "fetch-error") && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {(phase === "ready" || phase === "deleting" || phase === "deleted" || phase === "delete-error") && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <WarningOutlined className="mt-0.5" />
                    <span>This will permanently delete the schema and cannot be undone.</span>
                </div>
            )}

            {phase === "deleted" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Deleted.
                </div>
            )}
            {phase === "deleted" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> Delete could not be verified
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "delete-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Delete failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                    >
                        Delete
                    </button>
                </div>
            )}
            {phase === "deleting" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Deleting...
                </div>
            )}
        </div>
    );
}
